import { Mail, RotateCw, UserPlus, X } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import type { FormEvent } from "react";

import {
  ApiError,
  inviteMember,
  listMembers,
  resendInvite,
  revokeInvite,
  updateMember,
} from "@/api/client";
import type { Members, Role } from "@/api/client";
import { useCan } from "@/auth/AuthProvider";
import { Badge } from "@/ui/Badge";
import { Button } from "@/ui/Button";
import { cn } from "@/ui/cn";
import { Dialog } from "@/ui/Dialog";
import { Input } from "@/ui/Input";
import { Notice } from "@/ui/Notice";
import { PageHeader } from "@/ui/PageHeader";
import { Skeleton, Spinner } from "@/ui/primitives";
import { Table, Td, Th } from "@/ui/Table";

const ROLE_LABEL: Record<Role, string> = {
  owner: "Owner",
  admin: "Admin",
  estimator: "Estimator",
  viewer: "Viewer",
};

const SELECT =
  "h-9 w-full rounded-md border border-line-strong bg-surface px-2.5 text-sm text-ink focus:border-accent focus:outline-none";

function day(iso: string | null): string {
  if (!iso) return "Never";
  return new Date(iso).toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

function failure(err: unknown): string {
  return err instanceof ApiError ? err.detail : "Could not reach the server.";
}

/** Only an owner makes an owner. */
function assignable(canOwners: boolean): Role[] {
  return canOwners ? ["owner", "admin", "estimator", "viewer"] : ["admin", "estimator", "viewer"];
}

function InviteDialog({
  onClose,
  onSent,
}: {
  onClose: () => void;
  onSent: (email: string) => void;
}) {
  const roles = assignable(useCan("owners:manage"));
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<Role>("estimator");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await inviteMember(email.trim(), role);
      onSent(email.trim());
    } catch (err) {
      setError(failure(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()} label="Invite a member">
      <form className="space-y-4 p-4" onSubmit={(event) => void submit(event)}>
        <p className="text-sm text-ink-2">
          They get an email with a link to choose their name and password. The link works once and
          expires in 7 days.
        </p>
        {error ? <Notice tone="danger">{error}</Notice> : null}
        <label className="block space-y-1.5">
          <span className="text-sm font-medium text-ink">Email</span>
          <Input
            type="email"
            required
            autoFocus
            value={email}
            onChange={(event) => setEmail(event.target.value)}
          />
        </label>
        <label className="block space-y-1.5">
          <span className="text-sm font-medium text-ink">Role</span>
          <select
            className={SELECT}
            value={role}
            onChange={(event) => setRole(event.target.value as Role)}
          >
            {roles.map((r) => (
              <option key={r} value={r}>
                {ROLE_LABEL[r]}
              </option>
            ))}
          </select>
        </label>
        <div className="flex justify-end gap-2">
          <Button type="button" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" disabled={busy || !email.trim()}>
            {busy ? <Spinner className="size-4" /> : <Mail aria-hidden="true" />}
            Send invite
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

/**
 * Who is in the organization and who has been invited (ADR-0012). Owners and admins
 * invite, change roles and deactivate here; only an owner touches or makes an owner, and
 * the API keeps the last owner.
 */
export function MembersScreen() {
  const canManage = useCan("members:manage");
  const canOwners = useCan("owners:manage");
  const roles = assignable(canOwners);
  const [data, setData] = useState<Members | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState<string | null>(null);
  const [inviting, setInviting] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setData(await listMembers());
      setError(null);
    } catch (err) {
      setError(failure(err));
    }
  }, []);

  useEffect(() => {
    if (canManage) void load();
  }, [load, canManage]);

  const act = async (id: string, call: () => Promise<unknown>, done?: string) => {
    setBusyId(id);
    setSent(null);
    try {
      await call();
      await load();
      if (done) setSent(done);
    } catch (err) {
      setError(failure(err));
    } finally {
      setBusyId(null);
    }
  };

  if (!canManage) {
    return (
      <main className="mx-auto w-full max-w-6xl px-4 py-8 lg:px-8">
        <PageHeader title="Members" />
        <Notice tone="warn" className="mt-4">
          Only an owner or admin manages members.
        </Notice>
      </main>
    );
  }

  const seats = data?.seats;
  const activeOwners =
    data?.members.filter((m) => m.role === "owner" && m.status === "active").length ?? 0;
  /** An admin never touches an owner, and the API refuses to lose the last active one. */
  const fixed = (m: Members["members"][number]) =>
    m.role === "owner" && (!canOwners || (m.status === "active" && activeOwners === 1));
  return (
    <main className="mx-auto w-full max-w-6xl px-4 py-8 lg:px-8">
      <PageHeader
        title="Members"
        description={
          seats
            ? seats.licensed === null
              ? seats.used === 1
                ? "1 member or pending invite"
                : `${seats.used} members and pending invites`
              : `${seats.used} of ${seats.licensed} seats used`
            : "Who can sign in to your organization."
        }
        actions={
          <Button variant="primary" onClick={() => setInviting(true)}>
            <UserPlus aria-hidden="true" />
            Invite
          </Button>
        }
      />
      {error ? (
        <Notice tone="danger" className="mt-4">
          {error}
        </Notice>
      ) : null}
      {sent ? (
        <Notice tone="ok" className="mt-4">
          {sent}
        </Notice>
      ) : null}

      {data === null ? (
        <div className="mt-6 space-y-2" role="status" aria-label="Loading members">
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} className="h-12 w-full" />
          ))}
        </div>
      ) : (
        <div className="mt-6 space-y-8">
          <Table aria-label="Members">
            <thead>
              <tr>
                <Th>Name</Th>
                <Th className="hidden md:table-cell">Email</Th>
                <Th>Role</Th>
                <Th className="hidden lg:table-cell">Last signed in</Th>
                <Th className="w-0">
                  <span className="sr-only">Actions</span>
                </Th>
              </tr>
            </thead>
            <tbody>
              {data.members.map((m) => (
                <tr key={m.user_id}>
                  <Td className="font-medium text-ink">
                    {m.name || m.email}
                    {m.status === "deactivated" ? (
                      <Badge tone="neutral" className="mt-1 flex w-fit">
                        Deactivated
                      </Badge>
                    ) : null}
                  </Td>
                  <Td className="hidden text-ink-2 md:table-cell">{m.email}</Td>
                  <Td>
                    {fixed(m) ? (
                      ROLE_LABEL[m.role]
                    ) : (
                      <select
                        aria-label={`Role of ${m.name || m.email}`}
                        className={cn(SELECT, "h-8 w-auto text-[13px]")}
                        value={m.role}
                        disabled={busyId === m.user_id}
                        onChange={(event) =>
                          void act(m.user_id, () =>
                            updateMember(m.user_id, { role: event.target.value as Role }),
                          )
                        }
                      >
                        {roles.map((r) => (
                          <option key={r} value={r}>
                            {ROLE_LABEL[r]}
                          </option>
                        ))}
                      </select>
                    )}
                  </Td>
                  <Td className="hidden text-ink-2 lg:table-cell">{day(m.last_login_at)}</Td>
                  <Td className="text-right whitespace-nowrap">
                    {fixed(m) ? null : (
                      <Button
                        size="sm"
                        variant="ghost"
                        disabled={busyId === m.user_id}
                        onClick={() =>
                          void act(m.user_id, () =>
                            updateMember(m.user_id, {
                              status: m.status === "active" ? "deactivated" : "active",
                            }),
                          )
                        }
                      >
                        {m.status === "active" ? "Deactivate" : "Reactivate"}
                      </Button>
                    )}
                  </Td>
                </tr>
              ))}
            </tbody>
          </Table>

          {data.invites.length > 0 ? (
            <section className="space-y-2">
              <h2 className="text-sm font-semibold text-ink">Pending invites</h2>
              <Table aria-label="Pending invites">
                <thead>
                  <tr>
                    <Th>Email</Th>
                    <Th>Role</Th>
                    <Th className="hidden md:table-cell">Expires</Th>
                    <Th className="w-0">
                      <span className="sr-only">Actions</span>
                    </Th>
                  </tr>
                </thead>
                <tbody>
                  {data.invites.map((i) => (
                    <tr key={i.id}>
                      <Td className="text-ink">{i.email}</Td>
                      <Td>{ROLE_LABEL[i.role]}</Td>
                      <Td className="hidden text-ink-2 md:table-cell">
                        {i.expired ? <Badge tone="warn">Expired</Badge> : day(i.expires_at)}
                      </Td>
                      <Td className="whitespace-nowrap">
                        <div className="flex justify-end gap-1">
                          <Button
                            size="sm"
                            variant="ghost"
                            disabled={busyId === i.id}
                            onClick={() =>
                              void act(
                                i.id,
                                () => resendInvite(i.id),
                                `Invite sent again to ${i.email}.`,
                              )
                            }
                          >
                            <RotateCw aria-hidden="true" />
                            Resend
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            disabled={busyId === i.id}
                            onClick={() => void act(i.id, () => revokeInvite(i.id))}
                          >
                            <X aria-hidden="true" />
                            Revoke
                          </Button>
                        </div>
                      </Td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </section>
          ) : null}
        </div>
      )}

      {inviting ? (
        <InviteDialog
          onClose={() => setInviting(false)}
          onSent={(email) => {
            setInviting(false);
            setSent(`Invite sent to ${email}.`);
            void load();
          }}
        />
      ) : null}
    </main>
  );
}
